import React, {useEffect, useRef, useState} from "react";
import {IconButton} from "@material-ui/core";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import RotateRightIcon from "@material-ui/icons/RotateRight";
import CenterFocusStrongIcon from "@material-ui/icons/CenterFocusStrong";

// The restaurant's receipt photo, zoomable: mouse wheel zooms around the
// cursor, drag to move, double-click toggles 2.5×, buttons for zoom / rotate
// (couriers often shoot the receipt sideways) / reset.
const MIN_SCALE = 1;
const MAX_SCALE = 6;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default function ZoomableReceipt({src, alt}) {
  const [view, setView] = useState({scale: 1, x: 0, y: 0});
  const [rotation, setRotation] = useState(0);
  const [failed, setFailed] = useState(false);
  const stage = useRef(null);
  const drag = useRef(null);

  // Zoom by `factor`, keeping the point under (px, py) — relative to the
  // stage's centre — still.
  const zoomBy = (factor, px = 0, py = 0) => setView((current) => {
    const scale = clamp(current.scale * factor, MIN_SCALE, MAX_SCALE);
    if (scale === 1) {
      return {scale: 1, x: 0, y: 0};
    }
    const ratio = scale / current.scale;
    return {scale, x: px - (px - current.x) * ratio, y: py - (py - current.y) * ratio};
  });

  const pointFromEvent = (e) => {
    const rect = stage.current.getBoundingClientRect();
    return [e.clientX - rect.left - rect.width / 2, e.clientY - rect.top - rect.height / 2];
  };

  // A native listener: React's wheel handler is passive, so it couldn't stop
  // the dialog from scrolling while zooming.
  useEffect(() => {
    const element = stage.current;
    if (!element) {
      return undefined;
    }
    const onWheel = (e) => {
      e.preventDefault();
      const [px, py] = pointFromEvent(e);
      zoomBy(Math.exp(-e.deltaY / 300), px, py);
    };
    element.addEventListener("wheel", onWheel, {passive: false});
    return () => element.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failed]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {x: e.clientX, y: e.clientY, view};
  };

  const onPointerMove = (e) => {
    if (!drag.current || view.scale === 1) {
      return;
    }
    const start = drag.current;
    setView({...start.view, x: start.view.x + e.clientX - start.x, y: start.view.y + e.clientY - start.y});
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const onDoubleClick = (e) => {
    const [px, py] = pointFromEvent(e);
    zoomBy(view.scale > 1 ? 1 / view.scale : 2.5, px, py);
  };

  const reset = () => {
    setView({scale: 1, x: 0, y: 0});
    setRotation(0);
  };

  return (
    <div className="receipt-zoom">
      <div
        ref={stage}
        className="receipt-zoom-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        style={{cursor: view.scale > 1 ? "grab" : "zoom-in"}}>
        {failed ? <div className="receipt-zoom-missing">Bonul nu s-a putut încărca.</div> : (
          <img
            src={src}
            alt={alt}
            draggable={false}
            onError={() => setFailed(true)}
            style={{transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale}) rotate(${rotation}deg)`}}/>
        )}
      </div>
      <div className="receipt-zoom-toolbar">
        <IconButton size="small" title="Micșorează" onClick={() => zoomBy(1 / 1.4)}><ZoomOutIcon/></IconButton>
        <span className="receipt-zoom-level">{Math.round(view.scale * 100)}%</span>
        <IconButton size="small" title="Mărește" onClick={() => zoomBy(1.4)}><ZoomInIcon/></IconButton>
        <IconButton size="small" title="Rotește" onClick={() => setRotation((r) => (r + 90) % 360)}><RotateRightIcon/></IconButton>
        <IconButton size="small" title="Resetează" onClick={reset}><CenterFocusStrongIcon/></IconButton>
        <a className="receipt-zoom-open" href={src} target="_blank" rel="noopener noreferrer">Deschide originalul</a>
      </div>
    </div>
  );
}
